import React, { useState } from 'react'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import { Column, Row, Expaned } from '@/components/layout/layout'
import { getBEMCls } from '@/utils/classname'

import './stock-join-detail.scss'
import { IUser } from '@/model/types/user'
import FormTitle from '../../../components/form/form-title/form-title'
import { Goods, Unit } from '@/model/types/goods'
import { ISupplier } from '@/model/types/supllier'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'
import { useRequest, usePersistFn } from 'ahooks'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { Table, Spin } from 'antd'
import { ColumnsType } from 'antd/es/table'
import Button from '@/components/button/button'
import PurchaseDetail from '../../../purchase-manage/purchase-order/modules/purchase-detail/purchase-detail'
import { Modal } from '@/components/dialog/modal'
import Auth from '@/views/main/components/auth/auth'

interface OrderInfo {
    creator: { id: number; user: IUser }
    eta: string
    id: number
    order_goods_count: number
    order_no: string
    purchase_order_id: number
    purchase_time: string
    stock_in_time: string
    stocked_in: boolean
    stocked_in_amount: number
    stocking_in_amount: number
    source: 'purchase' | 'transfer'
    transfer_order_id: number | null
    transfer_order_no: string | null
}

interface GoodInfo {
    goods: Goods
    suppliers: ISupplier
    stock_in_unit: Unit
    stocked_in_amount: number
    stocking_in_amount: number
    id: number
}

interface StockJoinDetailProps {
    id: number
}

const StockJoinDetail: React.FC<StockJoinDetailProps> = props => {
    const cls = (...arg: string[]) => getBEMCls('stock-join-detail', ...arg)

    const [detailInfo, setDetailInfo] = useState<null | OrderInfo>(null)
    const [purchaseOrderNo, setPurchaseOrderNo] = useState<string>('')
    const [purchaseOrderId, setPurchaseOrderId] = useState<number>(0)
    const [goodInfo, setGoodInfo] = useState<GoodInfo[]>([])
    const [loading, setLoading] = useState<boolean>(true)

    const setAllData = useSetDataTogether()

    const isTransfer = detailInfo?.source === 'transfer'
    const columns: ColumnsType<GoodInfo> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods',
            dataIndex: 'goods',
            render: val => val.name,
        },
        {
            title: isTransfer ? '待入库量' : '采购量',
            key: 'stocking_in_amount',
            dataIndex: 'stocking_in_amount',
            render: (val, record) => `${val}${record.stock_in_unit.name}`,
        },
        {
            title: '入库量',
            key: 'stocked_in_amount',
            dataIndex: 'stocked_in_amount',
            align: 'right',
            render: (val, record) => `${val}${record.stock_in_unit.name}`,
        },
    ]

    useRequest(
        () =>
            $post({
                url: '/warehouse/getstockinorderdetail',
                params: {
                    order_id: props.id,
                },
            }),
        {
            onSuccess: res => {
                setAllData(() => {
                    setDetailInfo(res.order_info)
                    setGoodInfo(res.order_goods_infos)
                    if (res.order_info.source === 'purchase') {
                        setPurchaseOrderNo(res.purchase_order_info.order_no)
                        setPurchaseOrderId(res.purchase_order_info.id)
                    }
                    setLoading(false)
                })
            },
            onError: e => $msg('error', e.message),
        },
    )

    const deleteOrder = useRequest(
        () =>
            $post({
                url: '/warehouse/revokestockinorder',
                params: {
                    stock_in_order_id: props.id,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                layerManage.pop()
                $msg('success', '撤销成功')
            },
            onError: e => $msg('error', e.message),
        },
    )

    const handleCancel = usePersistFn(() => {
        showLayer(
            <Modal
                isShow={true}
                type="danger"
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    deleteOrder.run()
                }}
            >
                <div data-margin-top-16 data-margin-bottom-24>
                    <div data-color-000 data-font-weight-600 data-font-size-20>
                        确认撤销该入库单吗？
                    </div>
                    <div data-font-size-14 data-color-999 data-margin-top-8>
                        撤销入库后，将生成新的待入库单
                    </div>
                </div>
            </Modal>,
        )
    })

    return (
        <BaseDrawer
            title="入库单详情"
            isShow
            width={580}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column data-height-100p>
                <Expaned data-overflow-auto>
                    <Spin spinning={loading}>
                        <div className={cls('container')}>
                            <Column data-justify-content-center data-expaned>
                                <div className={cls('list-item')}>
                                    <div className="title coll">入库时间：</div>
                                    <div className="txt">{detailInfo?.purchase_time}</div>
                                </div>

                                <div data-margin-top-12 className={cls('list-item')}>
                                    <div className="title coll">操作人：</div>
                                    <Row className="txt" data-align-items-center>
                                        <img
                                            data-margin-right-6
                                            src={detailInfo?.creator?.user.avatar}
                                            alt=""
                                            className="avatar"
                                        />
                                        <div data-width-126 className="text-popper">
                                            {detailInfo?.creator?.user.realname || detailInfo?.creator?.user.nickname}
                                        </div>
                                    </Row>
                                </div>
                            </Column>
                            <Column data-justify-content-center data-expaned>
                                <div className={cls('list-item')}>
                                    <div className="title coll">入库单号：</div>
                                    <div className="txt">{detailInfo?.order_no}</div>
                                </div>

                                <div data-margin-top-12 className={cls('list-item')}>
                                    <div className="title coll">{isTransfer ? '关联调拨单号：' : '关联采购单号：'}</div>
                                    {isTransfer ? (
                                        <div className="txt">{detailInfo?.transfer_order_no || '-'}</div>
                                    ) : (
                                        <Auth
                                            perssion={31}
                                            onClick={() => {
                                                layerManage.pop()
                                                showLayer(<PurchaseDetail orderId={purchaseOrderId} />)
                                            }}
                                        >
                                            <div className="action">{purchaseOrderNo}</div>
                                        </Auth>
                                    )}
                                </div>
                            </Column>
                        </div>
                        <FormTitle title="商品清单" hasLine={false} />
                        <div className={cls('table-container')}>
                            <Table
                                columns={columns}
                                rowKey={r => r.id}
                                dataSource={goodInfo}
                                pagination={false}
                                bordered
                            ></Table>
                        </div>
                    </Spin>
                </Expaned>
                {!isTransfer && (
                    <div className={cls('footer')}>
                        <Button loading={deleteOrder.loading || void 0} types="error" onClick={handleCancel}>
                            作废
                        </Button>
                    </div>
                )}
            </Column>
        </BaseDrawer>
    )
}

export default StockJoinDetail
